import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { loginSchema, type LoginInput, authApi } from '../api/auth'
import { googleAuthApi, loadGoogleIdentityServices, type GoogleCredentialResponse } from '../api/google'
import { useAuthStore } from '../store/authStore'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined

export default function Login() {
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const [serverError, setServerError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const googleBtnRef = useRef<HTMLDivElement | null>(null)
  const [googleReady, setGoogleReady] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', recordar: false },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  })

  async function onSubmit(values: LoginInput) {
    setServerError(null)
    try {
      const res = await authApi.login({ email: values.email, password: values.password })
      login(res.token, res.refreshToken, res.usuario)
      navigate('/')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al iniciar sesión'
      const axiosMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      setServerError(axiosMsg ?? msg)
    }
  }

  // Google Identity Services setup
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleBtnRef.current) return

    let cancelled = false

    loadGoogleIdentityServices()
      .then(() => {
        if (cancelled || !window.google || !googleBtnRef.current) return

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response: GoogleCredentialResponse) => {
            setServerError(null)
            googleAuthApi
              .loginWithIdToken(response.credential)
              .then((res) => {
                login(res.token, res.refreshToken, res.usuario)
                navigate('/')
              })
              .catch((err: unknown) => {
                const axiosMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
                setServerError(axiosMsg ?? 'Error al iniciar con Google')
              })
          },
          auto_select: false,
          cancel_on_tap_outside: true,
        })

        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          width: 320,
          locale: 'es',
        })

        setGoogleReady(true)
      })
      .catch((err) => {
        console.warn('No se pudo cargar GIS:', err)
      })

    return () => {
      cancelled = true
    }
  }, [login, navigate])

  function handleGoogleFallback() {
    setServerError(
      'Google Sign-In no está configurado. Falta VITE_GOOGLE_CLIENT_ID en el frontend.',
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/30">
            <span className="text-white text-xl font-bold">AF</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">AuraFleet</h1>
          <p className="text-sm text-slate-500 mt-1">Panel Supervisor</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-5">Iniciar sesión</h2>

          {/* Google Sign-In */}
          {GOOGLE_CLIENT_ID ? (
            <div className="flex justify-center mb-4">
              <div ref={googleBtnRef} />
              {!googleReady && (
                <div className="text-xs text-slate-400">Cargando Google…</div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={handleGoogleFallback}
              className="w-full flex items-center justify-center gap-3 rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors mb-4"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
              </svg>
              Continuar con Google
            </button>
          )}

          {/* Divider */}
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-2 text-slate-400">o con email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            <Input
              {...register('email')}
              label="Correo electrónico"
              type="email"
              placeholder="supervisor@empresa.com"
              autoComplete="email"
              error={errors.email?.message}
            />

            {/* Password con ojito */}
            <div className="flex flex-col gap-1">
              <label htmlFor="password" className="text-sm font-medium text-slate-700">
                Contraseña
              </label>
              <div className="relative">
                <input
                  {...register('password')}
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className={[
                    'w-full rounded-lg border bg-white text-slate-900 placeholder:text-slate-400 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                    'pl-3 pr-10 py-2',
                    errors.password ? 'border-danger focus:ring-danger' : 'border-slate-300',
                  ].join(' ')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-slate-700"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password?.message && <p className="text-xs text-danger">{errors.password.message}</p>}
            </div>

            <div className="flex items-center gap-2">
              <input
                {...register('recordar')}
                id="recordar"
                type="checkbox"
                className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
              />
              <label htmlFor="recordar" className="text-sm text-slate-600">
                Recordar sesión
              </label>
            </div>

            {serverError && (
              <div
                role="alert"
                className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-danger"
              >
                {serverError}
              </div>
            )}

            <Button type="submit" loading={isSubmitting} className="w-full mt-2">
              Ingresar
            </Button>
          </form>

          {/* Demo credentials hint */}
          <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
            <p className="font-medium text-slate-700 mb-1">Demo:</p>
            <p>📧 admin@tiahuanaco-demo.aurafleet.com</p>
            <p>🔑 Demo1234!</p>
          </div>
        </div>

        <div className="text-center mt-6 space-y-2">
          <p className="text-xs text-slate-400">
            AuraFleet &copy; {new Date().getFullYear()} — Gestión de flota
          </p>
          <p className="text-xs text-slate-500 space-x-3">
            <a href="/terms" className="hover:text-primary hover:underline">Términos</a>
            <span className="text-slate-300">·</span>
            <a href="/privacy" className="hover:text-primary hover:underline">Privacidad</a>
            <span className="text-slate-300">·</span>
            <a href="/refunds" className="hover:text-primary hover:underline">Reembolsos</a>
          </p>
        </div>
      </div>
    </div>
  )
}
